'use client'

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StaffTransaction } from '@garage/shared'
import { STAFF_TRANSACTION_TYPE_LABELS } from '@garage/shared'
import {
  PageHeader,
  Button,
  IconButton,
  DataTable,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  KeyValue,
  useSnackbar,
  type Column,
  EditIcon,
  DeleteIcon,
  AddIcon,
  CheckIcon,
  ChevronDownIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters } from '@/lib/hooks.js'
import { ActivityInfo } from '@/components/ActivityInfo.js'
import { StaffTransactionDialog } from '@/components/StaffTransactionDialog.js'
import { StaffPaymentDialog } from '@/components/StaffPaymentDialog.js'

export function WorkerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const formatters = useFormatters()
  const { t } = useTranslation()

  const [editingTx, setEditingTx] = useState<StaffTransaction | undefined>()
  const [addingTx, setAddingTx] = useState(false)
  const [deleteTxId, setDeleteTxId] = useState<string | null>(null)
  const [payingTx, setPayingTx] = useState<StaffTransaction | undefined>()
  const [deletePaymentId, setDeletePaymentId] = useState<{ txId: string; paymentId: string } | null>(null)
  const [expandedPaymentTxIds, setExpandedPaymentTxIds] = useState<Set<string>>(new Set())

  const { data: worker, isLoading } = useQuery({
    queryKey: qk.worker(id ?? ''),
    queryFn: () => api.workers.get(id ?? ''),
    enabled: !!id,
  })

  const settleMutation = useMutation({
    mutationFn: (tx: StaffTransaction) =>
      api.workers.updateTransaction(id!, tx.id, {
        type: tx.type,
        amountMinor: tx.amountMinor,
        reason: tx.reason,
        occurredAt: tx.occurredAt,
        notes: tx.notes,
        settled: tx.settledAt === null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.worker(id!) })
      queryClient.invalidateQueries({ queryKey: qk.workers() })
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('workerDetail.failedToUpdate'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (txId: string) => api.workers.removeTransaction(id!, txId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.worker(id!) })
      queryClient.invalidateQueries({ queryKey: qk.workers() })
      snackbar.success(t('workerDetail.recordRemoved'))
      setDeleteTxId(null)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('workerDetail.failedToRemove'))
    },
  })

  const deletePaymentMutation = useMutation({
    mutationFn: (vars: { txId: string; paymentId: string }) =>
      api.workers.removePayment(id!, vars.txId, vars.paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.worker(id!) })
      queryClient.invalidateQueries({ queryKey: qk.workers() })
      snackbar.success(t('workerDetail.recordRemoved'))
      setDeletePaymentId(null)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('workerDetail.failedToRemove'))
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton lines={3} />
      </div>
    )
  }

  if (!worker) {
    return (
      <EmptyState title={t('workerDetail.notFound')} description={t('workerDetail.mayHaveBeenRemoved')} />
    )
  }

  const balanceLabel =
    worker.balanceMinor === 0
      ? t('staff.settledUp')
      : worker.balanceMinor > 0
        ? t('workerDetail.owesCompany', { amount: formatters.money(worker.balanceMinor) })
        : t('workerDetail.companyOwes', { amount: formatters.money(-worker.balanceMinor) })

  const transactionTypeLabel = (row: StaffTransaction) =>
    t(`workerDetail.transactionTypes.${row.type}`, STAFF_TRANSACTION_TYPE_LABELS[row.type])

  const isEligibleForPayment = (row: StaffTransaction): boolean => {
    return (
      (row.type === 'advance' || row.type === 'reimbursement') &&
      row.sourceStaffEventId === null &&
      row.settledAt === null
    )
  }

  const togglePaymentExpanded = (txId: string) => {
    const next = new Set(expandedPaymentTxIds)
    if (next.has(txId)) {
      next.delete(txId)
    } else {
      next.add(txId)
    }
    setExpandedPaymentTxIds(next)
  }

  const columns: Column<StaffTransaction>[] = [
    {
      key: 'date',
      header: t('calendar.date'),
      cell: (row) => formatters.date(row.occurredAt),
      width: 'w-28',
      wrap: true,
    },
    {
      key: 'type',
      header: t('workerDetail.whatHappened'),
      cell: (row) => transactionTypeLabel(row),
      wrap: true,
    },
    {
      key: 'reason',
      header: t('workerDetail.whatFor'),
      wrap: true,
      cell: (row) => (
        <div>
          <div>{row.reason}</div>
          {row.sourceStaffEventId && (
            <div className="text-label-sm text-on-surface-subtle">
              {row.type === 'overtime' ? t('workerDetail.autoFromCalendarOvertime') : t('workerDetail.autoFromCalendar')}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('workerDetail.amount'),
      cell: (row) => (
        <div className="space-y-2">
          <div>{formatters.money(row.amountMinor)}</div>
          {row.paidMinor > 0 && (
            <>
              {row.settledAt !== null ? (
                <div className="text-label-sm text-on-surface-variant">
                  {t('staffPaymentDialog.paidInFull')}
                </div>
              ) : (
                <>
                  <div className="text-label-sm text-on-surface-variant">
                    {t('staffPaymentDialog.paidProgress', {
                      paid: formatters.money(row.paidMinor),
                      remaining: formatters.money(row.remainingMinor),
                    })}
                  </div>
                  <div className="h-1 bg-surface-high rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{
                        width: `${Math.min(100, Math.round((row.paidMinor / row.amountMinor) * 100))}%`,
                      }}
                    />
                  </div>
                </>
              )}
              <button
                className="text-label-sm text-primary hover:text-primary-variant flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation()
                  togglePaymentExpanded(row.id)
                }}
              >
                <ChevronDownIcon
                  className={`w-4 h-4 transition-transform ${
                    expandedPaymentTxIds.has(row.id) ? 'rotate-180' : ''
                  }`}
                />
                {t('staffPaymentDialog.paymentCount', { count: row.payments.length })}
              </button>
              {expandedPaymentTxIds.has(row.id) && (
                <div className="space-y-2 pt-2 border-t border-outline-variant">
                  {row.payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-start justify-between gap-2 p-2 bg-surface-low rounded text-label-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-on-surface font-medium">
                          {formatters.money(payment.amountMinor)}
                        </div>
                        <div className="text-on-surface-variant text-xs">
                          {formatters.date(payment.paidAt)}
                        </div>
                        {payment.notes && (
                          <div className="text-on-surface-subtle text-xs mt-0.5 line-clamp-2">
                            {payment.notes}
                          </div>
                        )}
                      </div>
                      <IconButton
                        icon={<DeleteIcon className="w-4 h-4" />}
                        label={t('workerDetail.deleteRecord')}
                        variant="danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeletePaymentId({ txId: row.id, paymentId: payment.id })
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ),
      numeric: true,
      width: 'w-44',
      wrap: true,
    },
    {
      key: 'status',
      header: t('jobcards.columns.status'),
      cell: (row) => {
        const isDisabled = row.payments.length > 0 && row.settledAt !== null
        const statusButtonClasses = [
          'text-body-sm rounded-full px-3 py-1',
          isDisabled ? 'bg-surface-high text-on-surface-variant' : 'interactive',
          row.settledAt !== null
            ? 'bg-surface-high text-on-surface-variant'
            : 'bg-error-container text-on-error-container',
        ]
          .filter(Boolean)
          .join(' ')
        const statusButtonTitle = isDisabled
          ? t('staffPaymentDialog.deletePaymentToReopen')
          : row.payments.length > 0 && row.settledAt === null
            ? t('staffPaymentDialog.marksRemainingAsPaid')
            : undefined
        return (
          <span title={statusButtonTitle}>
            <button
              className={statusButtonClasses}
              onClick={(e) => {
                e.stopPropagation()
                settleMutation.mutate(row)
              }}
              disabled={settleMutation.isPending || isDisabled}
            >
              {row.settledAt !== null ? t('workerDetail.settled') : t('workerDetail.pending')}
            </button>
          </span>
        )
      },
      width: 'w-28',
      wrap: true,
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex gap-1 justify-end">
          {isEligibleForPayment(row) && (
            <IconButton
              icon={<AddIcon className="w-5 h-5" />}
              label={t('staffPaymentDialog.recordPayment')}
              variant="standard"
              onClick={(e) => {
                e.stopPropagation()
                setPayingTx(row)
              }}
            />
          )}
          <IconButton
            icon={<EditIcon className="w-5 h-5" />}
            label={t('workerDetail.editRecord')}
            variant="standard"
            onClick={(e) => {
              e.stopPropagation()
              setEditingTx(row)
            }}
          />
          <IconButton
            icon={<DeleteIcon className="w-5 h-5" />}
            label={t('workerDetail.deleteRecord')}
            variant="danger"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteTxId(row.id)
            }}
          />
        </div>
      ),
      width: 'w-36',
      wrap: true,
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={worker.name}
        subtitle={worker.phone || undefined}
        back={{ to: '/staff', label: t('staff.title') }}
        actions={
          <Button variant="filled" icon={<AddIcon className="w-5 h-5" />} onClick={() => setAddingTx(true)}>
            {t('workerDetail.addRecord')}
          </Button>
        }
      />

      <ActivityInfo entityType="worker" entityId={worker.id} />

      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <KeyValue
          label={t('staff.columns.department')}
          value={worker.departmentName || t('departmentForm.noDepartment')}
          numeric={false}
        />
        <KeyValue
          label={t('staff.columns.balance')}
          value={balanceLabel}
          total
          numeric={false}
        />
      </div>

      {worker.transactions.length === 0 ? (
        <EmptyState
          icon={<CheckIcon className="w-12 h-12 text-on-surface-variant" />}
          title={t('workerDetail.noRecordsYet')}
          description={t('workerDetail.addFirstRecord')}
          action={<Button variant="tonal" onClick={() => setAddingTx(true)}>{t('workerDetail.addRecord')}</Button>}
        />
      ) : (
        <div className="bg-surface-container border border-outline-variant rounded-lg">
          <DataTable<StaffTransaction>
            columns={columns}
            rows={worker.transactions}
            rowKey={(t) => t.id}
            stackOnMobile
            stackBelow="lg"
          />
        </div>
      )}

      <StaffTransactionDialog
        open={addingTx}
        workerId={worker.id}
        onClose={() => setAddingTx(false)}
      />

      <StaffTransactionDialog
        open={!!editingTx}
        workerId={worker.id}
        transaction={editingTx}
        onClose={() => setEditingTx(undefined)}
      />

      <ConfirmDialog
        open={!!deleteTxId}
        title={t('workerDetail.deleteRecordTitle')}
        message={t('workerDetail.deleteRecordMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTxId) deleteMutation.mutate(deleteTxId)
        }}
        onCancel={() => setDeleteTxId(null)}
      />

      <StaffPaymentDialog
        open={!!payingTx}
        workerId={worker.id}
        transaction={payingTx}
        onClose={() => setPayingTx(undefined)}
      />

      <ConfirmDialog
        open={!!deletePaymentId}
        title={t('staffPaymentDialog.deletePaymentTitle')}
        message={t('staffPaymentDialog.deletePaymentMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deletePaymentMutation.isPending}
        onConfirm={() => {
          if (deletePaymentId) deletePaymentMutation.mutate(deletePaymentId)
        }}
        onCancel={() => setDeletePaymentId(null)}
      />
    </div>
  )
}
